/**
 * Meshtastic BLE Connection Manager
 *
 * Handles BLE connection to Meshtastic devices using the Capacitor BLE bridge
 * for cross-platform support (native iOS/Android + web browser).
 *
 * ⚠️ EXPERIMENTAL: This integration is under active development.
 */

import {
  isBLEAvailable,
  initBLE,
  requestDevice,
  connect,
  disconnect as bridgeDisconnect,
  read as bridgeRead,
  write as bridgeWrite,
  startNotifications,
  uint8ToDataView,
  dataViewToUint8,
  type BridgedBleDevice,
} from '../bluetooth/capacitor-ble-bridge'

import {
  MESHTASTIC_SERVICE_UUID,
  TORADIO_CHARACTERISTIC_UUID,
  FROMRADIO_CHARACTERISTIC_UUID,
  FROMNUM_CHARACTERISTIC_UUID,
  RECONNECT_DELAY_MS,
  MAX_RECONNECT_ATTEMPTS,
  CONFIG_REQUEST_ID,
  CONFIG_TIMEOUT_MS,
  READ_LOOP_DELAY_MS,
  MESHTASTIC_MTU,
} from './constants'
import {
  decodeFromRadio,
  encodeWantConfig,
  encodeHeartbeat,
} from './protocol'
import type {
  MeshtasticDevice,
  MeshtasticConnectionStatus,
  MeshPacket,
  MyNodeInfo,
  NodeInfo,
  Channel,
} from './types'

// ============= Types =============

export interface MeshtasticConnectionEvents {
  onStatusChange?: (status: MeshtasticConnectionStatus) => void
  onPacketReceived?: (packet: MeshPacket) => void
  onMyInfoReceived?: (info: MyNodeInfo) => void
  onNodeInfoReceived?: (info: NodeInfo) => void
  onChannelReceived?: (channel: Channel) => void
  onConfigComplete?: () => void
  onError?: (error: Error) => void
  onDisconnect?: () => void
}

// ============= State =============

let currentDevice: MeshtasticDevice | null = null
let bridgedDevice: BridgedBleDevice | null = null
let connectionStatus: MeshtasticConnectionStatus = 'disconnected'
let eventHandlers: MeshtasticConnectionEvents = {}
let reconnectAttempts = 0
let isReadLoopRunning = false
let configReceived = false
let readLoopAbortController: AbortController | null = null
let stopFromNumNotify: (() => Promise<void>) | null = null

// ============= Public API =============

/**
 * Check if BLE is available (native or web)
 */
export function isMeshtasticBLEAvailable(): boolean {
  return isBLEAvailable()
}

/**
 * Get current connection status
 */
export function getMeshtasticStatus(): MeshtasticConnectionStatus {
  return connectionStatus
}

/**
 * Get current connected device
 */
export function getMeshtasticDevice(): MeshtasticDevice | null {
  return currentDevice
}

/**
 * Set event handlers
 */
export function setMeshtasticEventHandlers(handlers: MeshtasticConnectionEvents): void {
  eventHandlers = handlers
}

/**
 * Update connection status and notify listeners
 */
function setStatus(status: MeshtasticConnectionStatus): void {
  connectionStatus = status
  eventHandlers.onStatusChange?.(status)
  console.log('[Meshtastic] Status:', status)
}

/**
 * Scan for and connect to a Meshtastic device
 */
export async function connectToMeshtastic(): Promise<MeshtasticDevice> {
  if (!isMeshtasticBLEAvailable()) {
    throw new Error('BLE is not available on this device')
  }

  await initBLE()
  setStatus('scanning')

  try {
    // Request device with Meshtastic service
    bridgedDevice = await requestDevice({
      services: [MESHTASTIC_SERVICE_UUID],
      optionalServices: [MESHTASTIC_SERVICE_UUID],
    })

    if (!bridgedDevice) {
      throw new Error('No device selected')
    }

    setStatus('connecting')
    console.log('[Meshtastic] Connecting to:', bridgedDevice.name || bridgedDevice.deviceId)

    // Connect via bridge
    await connect(bridgedDevice, handleDisconnect)

    // Create device object (without web-specific GATT refs — bridge handles those)
    currentDevice = {
      id: bridgedDevice.deviceId,
      name: bridgedDevice.name || 'Meshtastic Device',
      device: bridgedDevice._webDevice,
      server: bridgedDevice._webServer,
      lastSeen: new Date(),
    }

    // Set up fromNum notifications for packet availability
    stopFromNumNotify = await startNotifications(
      bridgedDevice,
      MESHTASTIC_SERVICE_UUID,
      FROMNUM_CHARACTERISTIC_UUID,
      () => {
        // A change in fromNum indicates new data is available
        if (currentDevice) currentDevice.lastSeen = new Date()
      }
    )

    setStatus('connected')
    reconnectAttempts = 0

    // Start configuration
    await requestConfiguration()

    return currentDevice
  } catch (error) {
    setStatus('error')
    const err = error instanceof Error ? error : new Error(String(error))
    eventHandlers.onError?.(err)
    throw err
  }
}

/**
 * Disconnect from current device
 */
export function disconnectMeshtastic(): void {
  stopReadLoop()
  stopFromNumNotify?.()
  stopFromNumNotify = null

  if (bridgedDevice) {
    bridgeDisconnect(bridgedDevice).catch(() => {})
    bridgedDevice = null
  }

  currentDevice = null
  configReceived = false
  setStatus('disconnected')
  console.log('[Meshtastic] Disconnected')
}

/**
 * Send data to the Meshtastic device
 */
export async function sendToRadio(data: Uint8Array): Promise<void> {
  if (!bridgedDevice) {
    throw new Error('Not connected to Meshtastic device')
  }

  if (connectionStatus !== 'ready' && connectionStatus !== 'connected') {
    throw new Error(`Cannot send: connection status is ${connectionStatus}`)
  }

  try {
    // Fragment if necessary
    const chunks = fragmentData(data)

    for (const chunk of chunks) {
      await bridgeWrite(
        bridgedDevice,
        MESHTASTIC_SERVICE_UUID,
        TORADIO_CHARACTERISTIC_UUID,
        uint8ToDataView(chunk)
      )
      // Small delay between chunks
      if (chunks.length > 1) {
        await new Promise(resolve => setTimeout(resolve, 20))
      }
    }

    if (currentDevice) currentDevice.lastSeen = new Date()
  } catch (error) {
    console.error('[Meshtastic] Failed to send:', error)
    throw error
  }
}

/**
 * Send a heartbeat to keep connection alive
 */
export async function sendHeartbeat(): Promise<void> {
  const heartbeat = encodeHeartbeat()
  await sendToRadio(heartbeat)
}

// ============= Internal Functions =============

/**
 * Request device configuration
 */
async function requestConfiguration(): Promise<void> {
  if (!bridgedDevice) return

  setStatus('configuring')
  configReceived = false

  try {
    const configRequest = encodeWantConfig(CONFIG_REQUEST_ID)
    await sendToRadio(configRequest)

    startReadLoop()

    const configPromise = new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        if (!configReceived) {
          reject(new Error('Configuration timeout'))
        }
      }, CONFIG_TIMEOUT_MS)

      const checkConfig = setInterval(() => {
        if (configReceived) {
          clearTimeout(timeout)
          clearInterval(checkConfig)
          resolve()
        }
      }, 100)
    })

    await configPromise
    setStatus('ready')
    eventHandlers.onConfigComplete?.()
    console.log('[Meshtastic] Configuration complete')
  } catch (error) {
    console.error('[Meshtastic] Configuration failed:', error)
    setStatus('error')
    eventHandlers.onError?.(error instanceof Error ? error : new Error(String(error)))
  }
}

/**
 * Handle disconnect event
 */
function handleDisconnect(): void {
  console.log('[Meshtastic] Device disconnected')
  stopReadLoop()
  setStatus('disconnected')
  eventHandlers.onDisconnect?.()

  if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
    reconnectAttempts++
    console.log(`[Meshtastic] Reconnection attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`)
    setTimeout(() => {
      if (bridgedDevice) {
        attemptReconnect()
      }
    }, RECONNECT_DELAY_MS * reconnectAttempts)
  }
}

/**
 * Attempt to reconnect to the device
 */
async function attemptReconnect(): Promise<void> {
  if (!bridgedDevice) return

  setStatus('connecting')

  try {
    await connect(bridgedDevice, handleDisconnect)

    stopFromNumNotify = await startNotifications(
      bridgedDevice,
      MESHTASTIC_SERVICE_UUID,
      FROMNUM_CHARACTERISTIC_UUID,
      () => {
        if (currentDevice) currentDevice.lastSeen = new Date()
      }
    )

    setStatus('connected')
    reconnectAttempts = 0

    await requestConfiguration()
  } catch (error) {
    console.error('[Meshtastic] Reconnection failed:', error)
    if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      reconnectAttempts++
      setTimeout(attemptReconnect, RECONNECT_DELAY_MS * reconnectAttempts)
    } else {
      setStatus('error')
      eventHandlers.onError?.(new Error('Max reconnection attempts reached'))
    }
  }
}

/**
 * Start the read loop for incoming packets
 */
function startReadLoop(): void {
  if (isReadLoopRunning) return
  isReadLoopRunning = true
  readLoopAbortController = new AbortController()
  readLoop(readLoopAbortController.signal)
}

/**
 * Stop the read loop
 */
function stopReadLoop(): void {
  isReadLoopRunning = false
  readLoopAbortController?.abort()
  readLoopAbortController = null
}

/**
 * Main read loop for incoming packets
 */
async function readLoop(signal: AbortSignal): Promise<void> {
  while (isReadLoopRunning && !signal.aborted) {
    try {
      if (!bridgedDevice) break

      const value = await bridgeRead(
        bridgedDevice,
        MESHTASTIC_SERVICE_UUID,
        FROMRADIO_CHARACTERISTIC_UUID
      )

      if (value && value.byteLength > 0) {
        const data = dataViewToUint8(value)
        processFromRadio(data)
      }

      await new Promise(resolve => setTimeout(resolve, READ_LOOP_DELAY_MS))
    } catch (error) {
      if (!signal.aborted) {
        console.error('[Meshtastic] Read error:', error)
      }
    }
  }
}

/**
 * Process incoming FromRadio data
 */
function processFromRadio(data: Uint8Array): void {
  const fromRadio = decodeFromRadio(data)
  if (!fromRadio) return

  if (fromRadio.myInfo) {
    if (currentDevice) currentDevice.myInfo = fromRadio.myInfo
    console.log('[Meshtastic] My info:', fromRadio.myInfo)
    eventHandlers.onMyInfoReceived?.(fromRadio.myInfo)
  }

  if (fromRadio.nodeInfo) {
    console.log('[Meshtastic] Node info:', fromRadio.nodeInfo)
    eventHandlers.onNodeInfoReceived?.(fromRadio.nodeInfo)
  }

  if (fromRadio.channel) {
    console.log('[Meshtastic] Channel:', fromRadio.channel)
    eventHandlers.onChannelReceived?.(fromRadio.channel)
  }

  if (fromRadio.configCompleteId === CONFIG_REQUEST_ID) {
    configReceived = true
  }

  if (fromRadio.packet) {
    console.log('[Meshtastic] Packet received:', fromRadio.packet)
    eventHandlers.onPacketReceived?.(fromRadio.packet)
  }
}

/**
 * Fragment data for BLE MTU limits
 */
function fragmentData(data: Uint8Array): Uint8Array[] {
  if (data.length <= MESHTASTIC_MTU) {
    return [data]
  }

  const chunks: Uint8Array[] = []
  for (let i = 0; i < data.length; i += MESHTASTIC_MTU) {
    chunks.push(data.slice(i, i + MESHTASTIC_MTU))
  }
  return chunks
}

/**
 * Create a promise that rejects after timeout
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(message)), ms)
  })
  return Promise.race([promise, timeout])
}
